import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useFormState } from "hooks/useFormState";
import { Box, Button, Typography, TextField } from "@mui/material";
import SaveButton from "components/buttonsComponent/SaveButton";
import DownloadButton from "components/buttonsComponent/DownloadButton";
import { FaDownload } from "react-icons/fa";
import UserIdButton from "components/buttonsComponent/UserIdButton";
import ReportCountdownButton from "components/buttonsComponent/ReportCountdownButton";
import FieldRenderer from "components/forms/FieldRenderer";

// Constants
const FORM_WIDTH = 707;

const FIELD_STYLES = {
  text: {
    borderBottom: "1px solid #000",
    fontFamily: "DM Sans",
    fontSize: "14px",
    paddingBottom: "2px",
  },
  textarea: {
    "& .MuiOutlinedInput-root": {
      fontFamily: "DM Sans",
      fontSize: "14px",
    },
  },
  select: {
    width: "100%",
    padding: "8px",
    fontFamily: "DM Sans",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  label: {
    fontFamily: "DM Sans",
    fontSize: "14px",
  },
};

const ReusableFormComponent = ({
  title = "Download Form",
  subtitle = "Download Form Preview",
  formTitle = "",
  sections = [],
  extraBottomActions = [],
  showReportCountdownButton = false,
  downloadFileName = "form",
  downloadFileExtension = "png",
  formImageUrl = null,
  onNext,
  onPrevious,
  onBack,
  onSave,
  nextButtonText = "Next",
  backButtonText = "Back",
  downloadButtonTitle = "Download Form",
  showSaveButton = true,
  showDownloadButton = true,
  showSyncBtn = false,
  showNextButton = true,
  showPreviousButton = false,
  showBackButton = false,
  titleColor = "#02462E",
  subtitleColor = "#02462E",
  logoSrc = "/logo.png",
  logoAlt = "ESG Vision",
  formWidth = FORM_WIDTH,
}) => {
  const [isDownloaded, setIsDownloaded] = useState(false);
  const { formData, otherTextValues, handleInputChange, handleCheckboxChange, handleOtherTextChange } = useFormState(sections);

  // Memoized handlers
  const handleNext = useCallback(() => {
    onNext?.(formData);
  }, [onNext, formData]);

  const handleAddAnother = useCallback(() => {
    onNext?.(formData, { isAddAnother: true });
  }, [onNext, formData]);

  const handlePrevious = useCallback(() => {
    onPrevious?.();
  }, [onPrevious]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack(formData);
    } else {
      onPrevious?.();
    }
  }, [onBack, onPrevious, formData]);

  const handleSave = useCallback(() => {
    onSave?.(formData);
  }, [onSave, formData]);

  const handleDownloadClick = useCallback(() => {
    if (formImageUrl) {
      const link = document.createElement("a");
      link.href = formImageUrl;
      link.download = `${downloadFileName}.${downloadFileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 2000);
  }, [formImageUrl, downloadFileName, downloadFileExtension]);



  const containerStyles = useMemo(
    () => ({
      width: { xs: "100%", sm: "90%", md: `${formWidth}px` },
      margin: "0 auto",
      padding: { xs: 2, sm: 3, md: 4 },
      backgroundColor: "#fff",
      borderRadius: "12px",
      maxHeight: "70vh",
      overflowY: "auto",
    }),
    [formWidth]
  );

  const buttonStyles = useMemo(
    () => ({
      background: "#02462E",
      color: "#fff",
      "&:hover": {
        background: "#02462E",
      },
      textTransform: "none",
      width: formWidth,
      height: "64px",
      borderRadius: "10px",
      fontFamily: "DM Sans",
      fontSize: "24px",
      fontWeight: "700",
    }),
    [formWidth]
  );

  return (
    <>
      {/* Header */}
      <Box
        className="standards-header"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          [(theme) => theme.breakpoints.down("sm")]: {
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "stretch",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          {showReportCountdownButton && <ReportCountdownButton />}
          {showSaveButton && (
            <SaveButton>
              Save
            </SaveButton>
          )}
          <UserIdButton />
        </Box>
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        className="mb-4 mx-4"
        style={{
          color: titleColor,
          fontWeight: "bold",
          mb: 2,
          alignItems: "center",
          fontFamily: "DM Sans",
          fontSize: "24px",
          lineHeight: "42px",
        }}
      >
        {title}
      </Typography>

      {/* Subtitle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Typography
          variant="h6"
          className="my-4"
          sx={{
            color: subtitleColor,
            fontWeight: "bold",
            fontFamily: "DM Sans",
            fontSize: "24px",
            lineHeight: "42px",
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      {/* Form Container */}
      <Box sx={containerStyles}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            mb: 3,
            fontFamily: "DM Sans",
          }}
        >
          {formTitle}
        </Typography>

        {/* Form Sections */}
        {sections.map((section, sectionIndex) => (
          <Box key={sectionIndex} mb={4}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "20px",
                fontFamily: "DM Sans",
                mb: 2,
              }}
            >
              {section.title}
            </Typography>

            {section.fields?.map((field, fieldIndex) => (
              <Box
                key={fieldIndex}
                mb={field.type === "checkbox" ? 2 : 3}
                sx={{
                  fontFamily: "DM Sans",
                  fontSize: "14px",
                  backgroundColor: field.disabled ? "#f9f9f9" : "transparent",
                  padding: field.disabled ? "12px" : "0",
                  borderRadius: field.disabled ? "8px" : "0",
                  border: field.disabled ? "1px solid #e0e0e0" : "none",
                }}
              >
                <Typography
                  mb={1}
                  sx={{
                    fontWeight: field.disabled ? "bold" : "normal",
                    color: field.disabled ? "#333" : "inherit",
                  }}
                >
                  {field.label}
                  {field.required && <span style={{ color: "red" }}> *</span>}
                </Typography>
                <FieldRenderer
                  field={field}
                  sectionIndex={sectionIndex}
                  fieldIndex={fieldIndex}
                  formData={formData}
                  otherTextValues={otherTextValues}
                  handleInputChange={handleInputChange}
                  handleCheckboxChange={handleCheckboxChange}
                  handleOtherTextChange={handleOtherTextChange}
                />
              </Box>
            ))}
          </Box>
        ))}

        {/* Add for Next Value button - placed inside form content */}
        {extraBottomActions?.length > 0 && (
          <Box sx={{ mt: 5, mb: 6, textAlign: "center" }}>
            {extraBottomActions.map((action, index) => {
              if (action.type === "addNext") {
                return (
                  <Button
                    key={index}
                    variant="outlined"
                    size="medium"
                    // onClick={handleAddAnother}
                    sx={{
                      borderColor: "#1976d2",
                      color: "#1976d2",
                      textTransform: "none",
                      fontFamily: "DM Sans",
                      fontWeight: 500,
                      px: 5,
                      py: 1,
                      borderRadius: "8px",
                      "&:hover": {
                        borderColor: "#1565c0",
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    Add for Next Value: Click here to Add
                  </Button>
                );
              }
              return null;
            })}
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {showDownloadButton && (
          <DownloadButton
            title={downloadButtonTitle || "Download Form"}
            endIcon={<FaDownload style={{ fontSize: 18, marginLeft: 6 }} />}
            isDownloaded={isDownloaded}
            onClick={handleDownloadClick}
            sx={buttonStyles}
          />
        )}

        {showNextButton && (
          <Button
            variant="contained"
            sx={buttonStyles}
            onClick={handleNext}
          >
            {nextButtonText}
          </Button>
        )}

        {showBackButton && (
          <Button
            variant="contained"
            sx={buttonStyles}
            onClick={handleBack}
          >
            {backButtonText}
          </Button>
        )}
      </Box>
    </>
  );
};

export default ReusableFormComponent;